import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Visibility,
  CheckCircle,
  Pending,
  Refresh,
  Close,
  Search,
  FilterList,
  CalendarToday,
  Person,
  Category,
} from "@mui/icons-material";
import adminAxios from "../../utils/adminAxios";
import { toast } from "react-toastify";
import RequestDetails from "../requestDetails/requestDetails";
import { useTranslation } from "react-i18next";

const LiveAuctionRequests = () => {
  const { t } = useTranslation();
  const [request, setRequest] = useState(null);
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null, title: "" });
  const [refreshing, setRefreshing] = useState(false);
  
  // Table states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState('all');
  const [filterAnchor, setFilterAnchor] = useState(null);

  const stats = {
    total: requests.length,
    confirmed: requests.filter(req => req.confirmed).length,
    pending: requests.filter(req => !req.confirmed).length,
  };

  // Filter data
  useEffect(() => {
    let result = requests;

    // Apply search filter
    if (searchTerm) {
      result = result.filter(item =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(item => 
        statusFilter === 'confirmed' ? item.confirmed : !item.confirmed
      );
    }

    setFilteredRequests(result);
  }, [requests, searchTerm, statusFilter]);

  const handleConfirmRequest = (id) => {
    setLoading(true);
    adminAxios
      .patch("live-auction-requests/confirm-request", null, {
        params: { request_id: +id },
      })
      .then(() => {
        toast.success(t("dashboard.requests.toast.confirmSuccess"));
        setConfirmDialog({ open: false, id: null, title: "" });
        fetchRequests();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || t("dashboard.requests.toast.confirmError"));
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminAxios.get(
        "live-auction-requests/get-all-requests"
      );
      setRequests(response.data.data);
    } catch (err) {
      setError(err.message);
      toast.error(t("dashboard.requests.toast.loadError"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchRequests();
  };

  const handleViewDetails = (row) => {
    setRequest(row);
    setOpenModal(true);
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  if (loading && requests.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, fontFamily: "Cairo, sans-serif" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight="bold" color="primary">
            {t("dashboard.requests.title")}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-xl"
          >
            {t("dashboard.requests.refresh")}
          </Button>
        </Box>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="text-3xl font-bold">{stats.total}</div>
            <div className="text-lg opacity-90">{t("dashboard.requests.stats.total")}</div>
          </div>
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="text-3xl font-bold">{stats.confirmed}</div>
            <div className="text-lg opacity-90">{t("dashboard.requests.stats.confirmed")}</div>
          </div>
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="text-3xl font-bold">{stats.pending}</div>
            <div className="text-lg opacity-90">{t("dashboard.requests.stats.pending")}</div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <TextField
              fullWidth
              placeholder={t("dashboard.requests.search.placeholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              className="rounded-xl bg-white dark:bg-gray-800"
            />
          </div>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={(e) => setFilterAnchor(e.currentTarget)}
            className="rounded-xl min-w-[120px]"
          >
            {t("dashboard.requests.filters.button")}
          </Button>
        </div>

        {/* Filter Menu */}
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={() => setFilterAnchor(null)}
        >
          <MenuItem>
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilter === 'all'}
                  onChange={() => setStatusFilter('all')}
                />
              }
              label={t("dashboard.requests.filters.all")}
            />
          </MenuItem>
          <MenuItem>
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilter === 'confirmed'}
                  onChange={() => setStatusFilter('confirmed')}
                />
              }
              label={t("dashboard.requests.filters.confirmedOnly")}
            />
          </MenuItem>
          <MenuItem>
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilter === 'pending'}
                  onChange={() => setStatusFilter('pending')}
                />
              }
              label={t("dashboard.requests.filters.pendingOnly")}
            />
          </MenuItem>
        </Menu>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Custom Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden animate-fadeIn">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.requests.table.id")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.requests.table.auctionName")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.requests.table.auctionOwner")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.requests.table.category")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.requests.table.startDate")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.requests.table.endDate")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.requests.table.status")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.requests.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              {filteredRequests.map((request) => (
                <tr
                  key={request.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors animate-fadeIn"
                >
                  {/* ID */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white text-right">
                      #{request.id}
                    </div>
                  </td>

                  {/* Auction Title */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white text-right">
                      {request.title || t("dashboard.requests.notSpecified")}
                    </div>
                  </td>

                  {/* User */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-end">
                      <div className="ml-3 text-right">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {request.user?.name || t("dashboard.requests.notSpecified")}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          <Person className="inline w-4 h-4 ml-1" />
                          {t("dashboard.requests.userLabel")}
                        </div>
                      </div>
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-semibold">
                          {request.user?.name?.charAt(0) || "U"}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Category */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-end">
                      <span className="text-sm text-gray-900 dark:text-white ml-2">
                        {request.category?.name || t("dashboard.requests.notSpecified")}
                      </span>
                      <Category className="w-4 h-4 text-gray-400" />
                    </div>
                  </td>

                  {/* Start Date */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-end">
                      <span className="text-sm text-gray-900 dark:text-white ml-2">
                        {new Date(request.startDate).toLocaleDateString('ar-EG')}
                      </span>
                      <CalendarToday className="w-4 h-4 text-gray-400" />
                    </div>
                  </td>

                  {/* Expiry Date */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-end">
                      <span className="text-sm text-gray-900 dark:text-white ml-2">
                        {new Date(request.expiryDate).toLocaleDateString('ar-EG')}
                      </span>
                      <CalendarToday className="w-4 h-4 text-gray-400" />
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      request.confirmed 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                    }`}>
                      <div className="flex items-center">
                        {request.confirmed ? (
                          <>
                            <CheckCircle className="w-3 h-3 ml-1" />
                            {t("dashboard.requests.status.confirmed")}
                          </>
                        ) : (
                          <>
                            <Pending className="w-3 h-3 ml-1" />
                            {t("dashboard.requests.status.pending")}
                          </>
                        )}
                      </div>
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2 rtl:space-x-reverse justify-end">
                      <Tooltip title={t("dashboard.requests.actions.viewDetails")}>
                        <button
                          onClick={() => handleViewDetails(request)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors px-3 py-1 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        >
                          <Visibility className="w-4 h-4" />
                        </button>
                      </Tooltip>

                      <button
                        onClick={() => setConfirmDialog({ open: true, id: request.id, title: request.title })}
                        disabled={request.confirmed || loading}
                        className={`px-3 py-1 rounded-lg transition-all ${
                          request.confirmed
                            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600'
                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-center text-sm">
                          {request.confirmed ? (
                            <>
                              <CheckCircle className="w-3 h-3 ml-1" />
                              {t("dashboard.requests.actions.activated")}
                            </>
                          ) : (
                            <>
                              <Pending className="w-3 h-3 ml-1" />
                              {t("dashboard.requests.actions.activate")}
                            </>
                          )}
                        </div>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredRequests.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 text-lg">
              {t("dashboard.requests.empty.title")}
            </div>
            <div className="text-gray-500 dark:text-gray-400 text-sm mt-2">
              {searchTerm || statusFilter !== 'all'
                ? t("dashboard.requests.empty.tryAdjust")
                : t("dashboard.requests.empty.willAppear")}
            </div>
          </div>
        )}
      </div>

      {/* Request Count */}
      <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        {t("dashboard.requests.count", { count: filteredRequests.length })}
      </div>

      {/* Modals */}
      <RequestDetails
        request={request}
        open={openModal}
        setOpen={setOpenModal}
        onConfirm={fetchRequests}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null, title: "" })}
        maxWidth="sm"
        fullWidth
        className="rounded-2xl"
      >
        <DialogTitle className="flex justify-between items-center bg-gradient-to-r from-blue-500 to-purple-600 text-white">
          <span>{t("dashboard.requests.confirmDialog.title")}</span>
          <IconButton
            onClick={() => setConfirmDialog({ open: false, id: null, title: "" })}
            className="text-white"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent className="p-6">
          <Typography className="text-right">
            {t("dashboard.requests.confirmDialog.messagePrefix")}{" "}
            <strong className="text-blue-600"> "{confirmDialog.title}"</strong>
            {t("dashboard.requests.confirmDialog.messageSuffix")}
          </Typography>
          <Typography variant="body2" className="text-right text-gray-600 mt-2">
            {t("dashboard.requests.confirmDialog.note")}
          </Typography>
        </DialogContent>
        <DialogActions className="p-4 gap-2">
          <Button
            onClick={() => setConfirmDialog({ open: false, id: null, title: "" })}
            variant="outlined"
            className="rounded-xl"
          >
            {t("dashboard.requests.confirmDialog.cancel")}
          </Button>
          <Button
            onClick={() => handleConfirmRequest(confirmDialog.id)}
            variant="contained"
            className="rounded-xl bg-green-600 hover:bg-green-700"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {loading
              ? t("dashboard.requests.confirmDialog.confirming")
              : t("dashboard.requests.confirmDialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiveAuctionRequests;